import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Loader2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useEditMode } from './EditMode';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

/* Site-wide announcement ticker (Phase 98).
   - Thin campaign-blue bar directly below the main navigation on every
     public page. Hidden entirely when inactive or empty.
   - Text fades in from the right, scrolls left, fades out at the left
     (fade zones ~8 characters wide), looping seamlessly with no gap.
   - Pauses on hover and on keyboard focus. Reduced-motion users see
     stationary text instead.
   - Optional single link (internal or external; new-window links use
     rel="noopener noreferrer"). No link = plain non-clickable text.
   - Signed-in staff/admin get a discreet inline Edit control that
     visitors never see. Settings persist in MongoDB and apply instantly. */

const SPEED_PX_PER_SEC = { slow: 40, normal: 70, fast: 110 };
const DEFAULT_CFG = { active: false, text: '', link_url: '', link_new_tab: false, speed: 'normal' };

const isExternal = (url) => /^https?:\/\//i.test(url || '');

/* One copy of the announcement (text or link) followed by a separator dot. */
const TickerItem = React.forwardRef(({ cfg, hidden }, ref) => {
  const inner = <span className="at-text">{cfg.text}</span>;
  let content;
  if (!cfg.link_url) {
    content = inner;
  } else if (cfg.link_new_tab || isExternal(cfg.link_url)) {
    content = (
      <a
        href={cfg.link_url}
        target={cfg.link_new_tab ? '_blank' : undefined}
        rel={cfg.link_new_tab ? 'noopener noreferrer' : undefined}
        className="at-link"
        tabIndex={hidden ? -1 : 0}
        data-testid={hidden ? undefined : 'ticker-link'}
      >
        {inner}
      </a>
    );
  } else {
    content = (
      <Link to={cfg.link_url} className="at-link" tabIndex={hidden ? -1 : 0} data-testid={hidden ? undefined : 'ticker-link'}>
        {inner}
      </Link>
    );
  }
  return (
    <span ref={ref} className="at-item" aria-hidden={hidden ? 'true' : undefined}>
      {content}
      <span className="at-sep" aria-hidden="true">&bull;</span>
    </span>
  );
});
TickerItem.displayName = 'TickerItem';

/* Inline editor dialog: staff/admin only. */
const TickerEditor = ({ open, onOpenChange, cfg, onSaved }) => {
  const [form, setForm] = useState(cfg);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ ...DEFAULT_CFG, ...cfg }); }, [open, cfg]);

  const save = async () => {
    const text = (form.text || '').trim();
    if (form.active && !text) {
      toast.error('Enter the announcement text before turning the ticker on.');
      return;
    }
    const url = (form.link_url || '').trim();
    if (url && !(url.startsWith('/') && !url.startsWith('//')) && !/^https?:\/\//i.test(url)) {
      toast.error('Links must be an internal path starting with / or a full http(s):// web address.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.put('/admin/ticker', {
        text, link_url: url, link_new_tab: !!form.link_new_tab,
        active: !!form.active, speed: form.speed || 'normal',
      });
      onSaved({ ...DEFAULT_CFG, ...r.data });
      onOpenChange(false);
      toast.success(r.data.active ? 'Announcement ticker updated and live.' : 'Announcement ticker saved (currently off).');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the ticker settings.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ticker-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1a2744]">Announcement Ticker</DialogTitle>
          <DialogDescription className="text-xs">
            Shown as a thin blue bar under the menu on every public page. Turn it off to hide the bar completely.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-[#1a2744]">Announcement text</Label>
            <Textarea
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value.slice(0, 300) }))}
              placeholder="e.g. Join Steve at the Bethany town hall this Saturday at 10 a.m."
              className="mt-1 min-h-[70px]"
              maxLength={300}
              data-testid="ticker-text-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">{(form.text || '').length}/300 characters</p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-[#1a2744]">Optional link</Label>
            <Input
              value={form.link_url}
              onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
              placeholder="/volunteer or https://example.com (leave empty for plain text)"
              className="mt-1 h-10"
              data-testid="ticker-link-input"
            />
            <div className="flex items-center justify-between mt-2 gap-3">
              <Label htmlFor="ticker-newtab" className="text-xs text-slate-600">Open the link in a new window</Label>
              <Switch
                id="ticker-newtab"
                checked={!!form.link_new_tab}
                onCheckedChange={(v) => setForm((f) => ({ ...f, link_new_tab: v }))}
                data-testid="ticker-newtab-toggle"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-[#1a2744]">Scrolling speed</Label>
              <Select value={form.speed || 'normal'} onValueChange={(v) => setForm((f) => ({ ...f, speed: v }))}>
                <SelectTrigger className="mt-1 h-10 bg-white" data-testid="ticker-speed-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="slow">Slow</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="fast">Fast</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 h-10">
              <Label htmlFor="ticker-active" className="text-xs font-bold text-[#1a2744]">Ticker on</Label>
              <Switch
                id="ticker-active"
                checked={!!form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="ticker-active-toggle"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="h-10 font-bold btn-press" data-testid="ticker-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="ticker-save-button">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Track the user's reduced-motion preference
const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return reduced;
};

// Measure so one group of copies always covers the viewport: the track is
// two identical groups animated -50%, giving a seamless loop with no gap
// and no overlap. Duration derives from group width / chosen speed.
const useMarqueeMeasure = (cfg, reducedMotion) => {
  const [copies, setCopies] = useState(2);
  const [duration, setDuration] = useState(30);
  const viewportRef = useRef(null);
  const itemRef = useRef(null);

  const measure = useCallback(() => {
    const vp = viewportRef.current;
    const it = itemRef.current;
    if (!vp || !it || !cfg) return;
    const itemW = it.offsetWidth || 1;
    const n = Math.max(1, Math.ceil(vp.offsetWidth / itemW));
    setCopies(n);
    const pxPerSec = SPEED_PX_PER_SEC[cfg.speed] || SPEED_PX_PER_SEC.normal;
    setDuration(Math.max(6, (itemW * n) / pxPerSec));
  }, [cfg]);

  useLayoutEffect(() => { measure(); }, [measure, cfg, reducedMotion]);
  useEffect(() => {
    window.addEventListener('resize', measure);
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  return { copies, duration, viewportRef, itemRef };
};

// Admin-only strip shown when the ticker is off (visitors never see it)
const TickerOffStrip = ({ onEdit }) => (
  <div className="bg-slate-100 border-b border-slate-200 px-4 py-1 flex items-center justify-center gap-2" data-testid="ticker-admin-off-bar">
    <Megaphone className="h-3 w-3 text-slate-400" />
    <span className="text-[11px] font-semibold text-slate-500">Announcement ticker is off (visitors do not see this strip)</span>
    <button
      onClick={onEdit}
      className="text-[11px] font-bold text-[#1a2744] underline underline-offset-2 hover:text-[#24406f] px-1 py-0.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1a2744]"
      data-testid="ticker-edit-button"
    >
      Edit
    </button>
  </div>
);

// The scrolling (or static, under reduced motion) announcement bar itself
const TickerViewport = ({ cfg, reducedMotion, copies, duration, viewportRef, itemRef }) => {
  if (reducedMotion) {
    return (
      <div className="at-static" data-testid="ticker-static-text">
        <TickerItem cfg={cfg} hidden={false} ref={itemRef} />
      </div>
    );
  }
  // Two identical groups back-to-back: the -50% animation loops seamlessly.
  const items = [];
  for (let i = 0; i < copies; i += 1) {
    items.push(<TickerItem key={`a${i}`} cfg={cfg} hidden={i !== 0} ref={i === 0 ? itemRef : undefined} />);
  }
  for (let i = 0; i < copies; i += 1) {
    items.push(<TickerItem key={`b${i}`} cfg={cfg} hidden />);
  }
  return (
    <div className="at-viewport" ref={viewportRef}>
      <div className="at-track" style={{ '--at-duration': `${duration}s` }} data-testid="ticker-track">
        {items}
      </div>
    </div>
  );
};

export const AnnouncementTicker = () => {
  const { isAdmin } = useEditMode();
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(false);
  const reducedMotion = useReducedMotion();
  const { copies, duration, viewportRef, itemRef } = useMarqueeMeasure(cfg, reducedMotion);

  useEffect(() => {
    api.get('/ticker').then((r) => setCfg({ ...DEFAULT_CFG, ...(r.data || {}) })).catch(() => setCfg({ ...DEFAULT_CFG }));
  }, []);

  if (!cfg) return null;
  const live = cfg.active && (cfg.text || '').trim();

  // Hidden for visitors when off; staff/admin see a discreet re-enable strip.
  if (!live) {
    if (!isAdmin) return null;
    return (
      <>
        <TickerOffStrip onEdit={() => setEditing(true)} />
        <TickerEditor open={editing} onOpenChange={setEditing} cfg={cfg} onSaved={setCfg} />
      </>
    );
  }

  return (
    <div
      className="announce-ticker"
      role="region"
      aria-label="Announcement"
      tabIndex={0}
      data-testid="announcement-ticker"
    >
      <TickerViewport cfg={cfg} reducedMotion={reducedMotion} copies={copies} duration={duration} viewportRef={viewportRef} itemRef={itemRef} />
      {isAdmin && (
        <button
          onClick={() => setEditing(true)}
          className="at-edit-btn"
          aria-label="Edit announcement ticker"
          title="Edit announcement ticker"
          data-testid="ticker-edit-button"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
      <TickerEditor open={editing} onOpenChange={setEditing} cfg={cfg} onSaved={setCfg} />
    </div>
  );
};
